import type { Locale } from '@/brand/i18n';

/**
 * Every string the portal shows, in both languages.
 *
 * The dictionary is a plain typed object rather than a runtime-loaded bundle: the whole
 * app is two screens, so the cost of shipping both languages is a few kilobytes, and in
 * exchange TypeScript catches a missing translation at build time instead of rendering a
 * key to a user.
 *
 * Placeholders are `{name}` and filled with `fill()` from the locale provider.
 */
export interface Dictionary {
  app: { name: string; suite: string; language: string; signOut: string };
  auth: {
    title: string; subtitle: string; email: string; password: string;
    showPassword: string; hidePassword: string; signingIn: string; signIn: string;
    failed: string; devAccounts: string; devNote: string;
    heroTitle: string; heroBody: string; arabicTagline: string;
  };
  chat: {
    newConversation: string; conversations: string; noConversations: string;
    heading: string; strapline: string;
    emptyTitle: string; emptyBody: string;
    placeholder: string; inputLabel: string; ask: string;
    thinking: string; loadingThread: string;
    grounding: string;
    loadFailed: string; answerFailed: string;
  };
  message: {
    sources: string; sourcesOne: string; figures: string;
    confidence: string; helpful: string; notHelpful: string;
    verification: Record<'MATCH' | 'PARTIAL_MATCH' | 'NEEDS_REVIEW' | 'SOURCE_UNAVAILABLE' | 'UNVERIFIED', string>;
  };
  panel: {
    title: string; subtitle: string; close: string; ariaLabel: string;
    verification: string; verificationNote: string; recheck: string; checking: string;
    calculations: string; calculationsNote: string;
    dateRange: string; interpretedAs: string;
    retrievals: string; source: string; records: string; retrieved: string; checksum: string;
    truncated: string; showRecords: string; hideRecords: string;
    fetchingRecords: string; recordsUnavailable: string; recordsExactly: string;
    followUps: string;
  };
  /**
   * Display names for computed figures, keyed by the canonical English label the AI API
   * stores. The stored label stays English so verification, the admin screens and the API
   * contract read the same regardless of who is looking; only the display changes.
   */
  figures: Record<string, string>;
  units: Record<string, string>;
  entities: Record<string, string>;
}

const EN: Dictionary = {
  app: { name: 'Viganium AI', suite: 'Portal', language: 'Language', signOut: 'Sign out' },
  auth: {
    title: 'Sign in',
    subtitle: 'Use your Nuat platform account.',
    email: 'Email',
    password: 'Password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    signingIn: 'Signing in…',
    signIn: 'Sign in',
    failed: 'Sign-in failed',
    devAccounts: 'Development accounts',
    devNote: 'Shown because this app is running on localhost.',
    heroTitle: 'Ask your business a question',
    heroBody:
      'Every answer is computed from the company’s own records, and shows the invoices, '
      + 'orders and stock lines it was derived from.',
    arabicTagline: 'نواة بياناتك',
  },
  chat: {
    newConversation: 'New conversation',
    conversations: 'Conversations',
    noConversations: 'No conversations yet.',
    heading: 'Viganium AI',
    strapline: 'Answers computed from your records — every figure cites its source.',
    emptyTitle: 'Ask about your business',
    emptyBody:
      'Every answer is computed from the company’s own records. Open the sources panel on '
      + 'any reply to see the invoices and orders behind it.',
    placeholder: 'Ask about sales, branches, stock, returns…',
    inputLabel: 'Your question',
    ask: 'Ask',
    thinking: 'Retrieving records and computing figures…',
    loadingThread: 'Loading conversation',
    grounding: 'Viganium AI answers only from retrieved records. If the data is not there, it says so.',
    loadFailed: 'Could not load that conversation',
    answerFailed: 'The answer could not be produced',
  },
  message: {
    sources: '{count} sources',
    sourcesOne: '1 source',
    figures: '{count} figures',
    confidence: 'confidence {percent}%',
    helpful: 'Helpful',
    notHelpful: 'Not helpful',
    verification: {
      MATCH: 'Match',
      PARTIAL_MATCH: 'Partial match',
      NEEDS_REVIEW: 'Needs review',
      SOURCE_UNAVAILABLE: 'Source unavailable',
      UNVERIFIED: 'Unverified',
    },
  },
  panel: {
    title: 'Sources',
    subtitle: 'Everything behind this answer',
    close: 'Close sources panel',
    ariaLabel: 'Sources and verification',
    verification: 'Verification',
    verificationNote:
      'Re-runs every retrieval this answer cited and compares checksums. A mismatch means the '
      + 'underlying records changed after the answer was written — which is legitimate, and '
      + 'worth knowing.',
    recheck: 'Re-check against source',
    checking: 'Checking…',
    calculations: 'Figures and how each was computed',
    calculationsNote:
      'Every figure above was calculated in code from retrieved records. The language model '
      + 'was given these numbers — it did not produce them.',
    dateRange: 'Date range applied',
    interpretedAs: 'Interpreted as',
    retrievals: 'Retrievals ({count})',
    source: 'Source',
    records: 'Records',
    retrieved: 'Retrieved',
    checksum: 'Checksum',
    truncated: 'This result was truncated — figures derived from it are lower bounds.',
    showRecords: 'Show the original records',
    hideRecords: 'Hide records',
    fetchingRecords: 'Fetching records',
    recordsUnavailable: 'The retrieval could not be read back from the connector.',
    recordsExactly: '{count} record(s), exactly as the answer received them.',
    followUps: 'Follow-up questions',
  },
  figures: {},
  units: {},
  entities: {},
};

const AR: Dictionary = {
  app: { name: 'Viganium AI', suite: 'البوابة', language: 'اللغة', signOut: 'تسجيل الخروج' },
  auth: {
    title: 'تسجيل الدخول',
    subtitle: 'استخدم حسابك على منصة نواة.',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    showPassword: 'إظهار كلمة المرور',
    hidePassword: 'إخفاء كلمة المرور',
    signingIn: 'جارٍ تسجيل الدخول…',
    signIn: 'تسجيل الدخول',
    failed: 'تعذّر تسجيل الدخول',
    devAccounts: 'حسابات التطوير',
    devNote: 'تظهر لأن التطبيق يعمل على localhost.',
    heroTitle: 'اسأل عن أعمالك',
    heroBody:
      'كل إجابة محسوبة من سجلات الشركة نفسها، وتعرض الفواتير والطلبات وأرصدة المخزون '
      + 'التي اشتُقّت منها.',
    arabicTagline: 'نواة بياناتك',
  },
  chat: {
    newConversation: 'محادثة جديدة',
    conversations: 'المحادثات',
    noConversations: 'لا توجد محادثات بعد.',
    heading: 'Viganium AI',
    strapline: 'إجابات محسوبة من سجلاتك — كل رقم يذكر مصدره.',
    emptyTitle: 'اسأل عن أعمالك',
    emptyBody:
      'كل إجابة محسوبة من سجلات الشركة نفسها. افتح لوحة المصادر في أي رد لترى الفواتير '
      + 'والطلبات التي وراءه.',
    placeholder: 'اسأل عن المبيعات أو الفروع أو المخزون أو المرتجعات…',
    inputLabel: 'سؤالك',
    ask: 'اسأل',
    thinking: 'جارٍ استرجاع السجلات وحساب الأرقام…',
    loadingThread: 'جارٍ تحميل المحادثة',
    grounding: 'يجيب Viganium AI من السجلات المسترجعة فقط. وإن لم تكن البيانات موجودة، يقول ذلك.',
    loadFailed: 'تعذّر تحميل هذه المحادثة',
    answerFailed: 'تعذّر إنتاج الإجابة',
  },
  message: {
    sources: '{count} مصادر',
    sourcesOne: 'مصدر واحد',
    figures: '{count} أرقام',
    confidence: 'الثقة {percent}%',
    helpful: 'مفيدة',
    notHelpful: 'غير مفيدة',
    verification: {
      MATCH: 'مطابقة',
      PARTIAL_MATCH: 'مطابقة جزئية',
      NEEDS_REVIEW: 'تحتاج مراجعة',
      SOURCE_UNAVAILABLE: 'المصدر غير متاح',
      UNVERIFIED: 'غير مُتحقَّق منها',
    },
  },
  panel: {
    title: 'المصادر',
    subtitle: 'كل ما وراء هذه الإجابة',
    close: 'إغلاق لوحة المصادر',
    ariaLabel: 'المصادر والتحقّق',
    verification: 'التحقّق',
    verificationNote:
      'يعيد تشغيل كل عملية استرجاع استشهدت بها الإجابة ويقارن البصمات. عدم التطابق يعني أن '
      + 'السجلات تغيّرت بعد كتابة الإجابة — وهذا أمر مشروع، ويستحق المعرفة.',
    recheck: 'إعادة التحقّق من المصدر',
    checking: 'جارٍ التحقّق…',
    calculations: 'الأرقام وكيف حُسب كل منها',
    calculationsNote:
      'كل رقم أعلاه حُسب برمجيًا من السجلات المسترجعة. النموذج اللغوي تلقّى هذه الأرقام — ولم ينتجها.',
    dateRange: 'النطاق الزمني المطبَّق',
    interpretedAs: 'فُهم على أنه',
    retrievals: 'عمليات الاسترجاع ({count})',
    source: 'المصدر',
    records: 'السجلات',
    retrieved: 'وقت الاسترجاع',
    checksum: 'البصمة',
    truncated: 'اقتُطعت هذه النتيجة — والأرقام المشتقّة منها حدود دنيا.',
    showRecords: 'عرض السجلات الأصلية',
    hideRecords: 'إخفاء السجلات',
    fetchingRecords: 'جارٍ جلب السجلات',
    recordsUnavailable: 'تعذّرت إعادة قراءة الاسترجاع من الموصّل.',
    recordsExactly: '{count} سجل، تمامًا كما تلقّتها الإجابة.',
    followUps: 'أسئلة للمتابعة',
  },
  figures: {

    'Total sales': 'إجمالي المبيعات',
    'Invoices issued': 'الفواتير الصادرة',
    'Average order value': 'متوسط قيمة الطلب',
    'Online sales': 'المبيعات عبر الإنترنت',
    'Branch sales': 'مبيعات الفروع',
    'Payment success rate': 'معدل نجاح المدفوعات',
    'Return rate': 'معدل المرتجعات',
    'New customers': 'العملاء الجدد',
    'Online share of sales': 'حصة الإنترنت من المبيعات',
    'Days with sales': 'أيام بها مبيعات',
    'Best day': 'أفضل يوم',
    'Average daily sales': 'متوسط المبيعات اليومية',
    'Branches with sales': 'الفروع التي حققت مبيعات',
    'Top branch': 'أفضل فرع',
    'Top product': 'أفضل منتج',
    'Products sold': 'المنتجات المباعة',
    'Units available': 'الوحدات المتاحة',
    'Units reserved': 'الوحدات المحجوزة',
    'Low-stock lines': 'أصناف قارب مخزونها على النفاد',
    'Most common return reason': 'أكثر أسباب الإرجاع شيوعًا',
    'Total customers': 'إجمالي العملاء',
    'VIP customers': 'عملاء VIP',
  },
  units: {

    EGP: 'ج.م',
    invoices: 'فاتورة',
    customers: 'عميل',
    branches: 'فرع',
    products: 'منتج',
    days: 'يوم',
    units: 'وحدة',
    lines: 'صنف',
    records: 'سجل',
    '%': '%',
  },
  entities: {
    invoices: 'الفواتير',
    orders: 'الطلبات',
    payments: 'المدفوعات',
    customers: 'العملاء',
    products: 'المنتجات',
    returns: 'المرتجعات',
    analytics: 'التحليلات',
    invoice: 'الفواتير',
    order: 'الطلبات',
    payment: 'المدفوعات',
    customer: 'العملاء',
    product: 'المنتجات',
    return: 'المرتجعات',
  },
};

const DICTIONARIES: Record<Locale, Dictionary> = { en: EN, ar: AR };

export function getDictionary(locale: Locale): Dictionary {
  return DICTIONARIES[locale] ?? EN;
}
